<template>
	<div class="logFileContainer">
		<div class="logFile">
			<code
				id="virtual-auctioneer-log-file"
				:class="logStyle"
				title="Read-only"
			>
				{{ stream.join("\n") }}
			</code>
			<div class="read-only-badge">Read-only</div>
			<div class="metrics-badge">
				CPU: {{ info.cpuUsage || 0 }}% : MEM: {{ info.memoryUsage || 0 }}%
			</div>
			<div class="bottom-controls tabContainer">
				<div class="extra-controls">
					<a v-if="activeAuction" class="link" @click="exportLogs()"
						>Export Logs</a
					>
					<a v-if="eventSource" class="link" @click="toggleLogs()"
						>Pause Logs</a
					>
					<a v-else-if="info.active" class="link" @click="toggleLogs()"
						>Resume Logs</a
					>
				</div>
				<div class="display-style">
					<div>
						<label>
							<input
								v-model="logStyle"
								type="radio"
								name="virtual_auctioneer_log_style"
								value="classic"
							/>
							<small>Classic</small>
						</label>
					</div>
					<div>
						<label>
							<input
								v-model="logStyle"
								type="radio"
								name="virtual_auctioneer_log_style"
								value="default"
							/>
							<small>Default</small>
						</label>
					</div>
				</div>
			</div>
		</div>
		<!-- Generic Alert -->
		<AlertComponent
			v-if="showAlert"
			v-bind:title="alertTitle"
			v-bind:message="alertMessage"
			v-bind:secondaryAction="null"
			@close="closeAlert()"
		>
		</AlertComponent>
	</div>
</template>

<script>
import apiMixin from "@/mixins/apiMixin.js";
import moment from "moment";
import AlertComponent from "@/components/Shared/AlertComponent.vue";

export default {
	name: "VirtualAuctioneerLogStream",
	components: {
		AlertComponent,
	},
	mixins: [apiMixin],
	props: ["activeAuction", "info"],
	data() {
		return {
			logStyle: "default",
			stream: [],
			eventSource: null,
			pauseLogs: false,
			// Alerts
			showAlert: false,
			alertTitle: "",
			alertMessage: "",
		};
	},
	computed: {
		logDate: function () {
			return moment().format("ddd MMM DD HH:mm:ss");
		},
	},
	mounted() {
		this.$root.$on("virtual-auctioneer-info-updated", (info) => {
			if (info.active && !this.eventSource && !this.pauseLogs) {
				this.subscribeToSSE();
			} else if (!info.active && this.eventSource) {
				this.unsubscribeFromSSE();
			}
		});
	},
	beforeDestroy() {
		this.unsubscribeFromSSE();
	},
	methods: {
		/**
		 * Close the alert
		 */
		closeAlert() {
			this.showAlert = false;
			if (this.shouldCloseModalAndNotExit === false) {
				this.exit();
			}
		},
		/**
		 * Subscribe to the SSE
		 */
		subscribeToSSE() {
			this.eventSource = new EventSource(
				`/api/active_auction/?action=virtualauctioneer:log`,
				{
					withCredentials: true,
				}
			);
			this.eventSource.onopen = () => {
				// console.debug('SSE connection opened');
			};
			this.eventSource.onmessage = (event) => {
				try {
					const data = JSON.parse(event.data);
					// console.info('Parsed VIRTUAL_AUCTIONEER_LOG data: ', data);
					this.stream = [...this.stream, ...data.data];

					// Keep the stream to a maximum of 1000 lines
					if (this.stream.length > 1000) {
						this.stream = this.stream.slice(-1000);
					}

					// Scroll to the bottom of the code block
					this.$nextTick(() => {
						const codeBlock = this.$el.querySelector("code");
						codeBlock.scrollTop = codeBlock.scrollHeight;
					});
				} catch (error) {
					console.error("Error parsing JSON: ", error);
				}
			};
			this.eventSource.onerror = (error) => {
				console.error("SSE error: ", error);
			};
			window.addEventListener("beforeunload", this.unsubscribeFromSSE);
		},

		/**
		 * Unsubscribe from the SSE
		 */
		unsubscribeFromSSE() {
			if (this.eventSource) {
				this.eventSource.close();
				this.eventSource = null;
			}
			window.removeEventListener("beforeunload", this.unsubscribeFromSSE);
		},

		/**
		 * Download the virtual auctioneer log file
		 */
		exportLogs() {
			let apiPayload = {};
			apiPayload.apiEndpoint = "active_auction";
			apiPayload.postData = {};
			apiPayload.postData.action = "logs:export:virtualauctioneer";
			apiPayload.postData.auction_id = this.activeAuction?.id;

			// If there is no active auction id, show an alert
			if (!apiPayload.postData.auction_id) {
				this.alertTitle = "Error";
				this.alertMessage = "No active auction id found";
				this.showAlert = true;
				return;
			}

			this.loadingTitle = "Downloading log file";

			fetch("/api/active_auction/", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-CSRFToken": this.$store.getters.csrfToken,
				},
				body: JSON.stringify(apiPayload.postData),
			})
				.then((response) => {
					if (response.ok) return response.blob();
					this.alertTitle = "Error";
					switch (response.status) {
						case 403:
							this.alertMessage =
								"You do not have permission to download the log file";
							break;
						case 404:
							this.alertMessage = "The log file could not be found";
							break;
						default:
							this.alertMessage =
								"An error occurred while downloading the log file";
					}
					this.showAlert = true;
					throw new Error("Network response was not ok");
				})
				.then((blob) => {
					const url = window.URL.createObjectURL(blob);
					const a = document.createElement("a");
					a.href = url;
					a.download = `${this.activeAuction?.id}_virtual_auctioneer_log.zip`;
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
					window.URL.revokeObjectURL(url);
				})
				.catch((error) => console.error("Download error:", error));
		},

		/**
		 * Pause/resume the logs
		 */
		toggleLogs() {
			this.pauseLogs = !this.pauseLogs;
			if (this.eventSource) {
				this.unsubscribeFromSSE();
			} else {
				this.subscribeToSSE();
			}
		},
	},
};
</script>

<style scoped>
button:disabled {
	cursor: not-allowed;
	opacity: 0.5;
}

.logFileContainer {
	display: flex;
	flex-direction: column;
	max-width: 1050px;
	margin: auto;
	margin-top: 1rem;
}

.logFile {
	position: relative;
	display: flex;
	flex-direction: column;
	height: 420px;
	max-width: 1050px;
	margin: auto;
	width: 100%;
	padding: 1rem;
}

.logFile code {
	height: 100%;
	overflow-y: scroll;
	word-wrap: break-word;
	white-space: pre-line;
	line-height: 100%;
}

.logFile code.classic {
	background-color: #1e1e1e;
	color: #c7c7c7;
	font-family: Menlo, Monaco, "Courier New", monospace;
	line-height: 1.5;
	padding: 16px;
}

.logFile .read-only-badge {
	position: absolute;
	top: 10px;
	right: 35px;
	background-color: transparent;
	padding: 0.25em 0.5em;
	font-size: 10px;
}

.logFile .metrics-badge {
	position: absolute;
	bottom: 50px;
	right: 35px;
	background-color: transparent;
	padding: 0 0.5em;
	font-size: 10px;
	border-radius: 10px;
}

.logFile .read-only-badge {
	color: limegreen;
}

.logFile .metrics-badge {
	background-color: limegreen;
	color: white;
	line-height: 150%;
}

.logFile .bottom-controls {
	gap: 0.5em;
}

.logFile .bottom-controls {
	padding-top: 0.5em;
}

.logFile .bottom-controls .link {
	cursor: pointer;
	font-size: 0.8em;
	padding: 0 0.5em;
}

.logFile .bottom-controls .display-style {
	display: flex;
	gap: 0.5em;
}

.logFile .bottom-controls .display-style label {
	display: flex;
	gap: 0.25em;
	cursor: pointer;
}

.logFile .bottom-controls .extra-controls {
	flex: 1;
}
</style>
